import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatErr, imgUrl } from "@/lib/api";
import { NEEDLE_ZONE_OPTIONS } from "@/components/HiddenNeedle";

const POSITION_OPTIONS = NEEDLE_ZONE_OPTIONS;

/**
 * AdminContest — Hunt-the-Needle contest management pane rendered inside
 * Admin.jsx when tab === "contest". Kept as its own file to avoid growing
 * Admin.jsx (already on the refactor list).
 *
 * Actions:
 *   • Start / edit / rotate the active contest (change target listing weekly)
 *   • View entrants + opt-in count
 *   • Compose an email broadcast to every opted-in entrant
 *   • Reset entries (type "DELETE ENTRIES" to confirm)
 *   • Start a NEW contest (archives previous + purges its entries)
 *   • Download entries CSV
 */
export default function AdminContest() {
  const [loading, setLoading] = useState(true);
  const [contestSummary, setContestSummary] = useState(null); // {contest, entry_count, opt_in_count}
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({ name: "", blurb: "", target_listing_id: "", target_position: "image-bottom", is_closed: false });
  const [saving, setSaving] = useState(false);

  const [sportFilter, setSportFilter] = useState("");
  const [listings, setListings] = useState([]);
  const [sports, setSports] = useState([]);

  const [randomBusy, setRandomBusy] = useState(false);

  // Broadcast composer
  const [broadcastSubject, setBroadcastSubject] = useState("");
  const [broadcastBody, setBroadcastBody] = useState("");
  const [broadcastBusy, setBroadcastBusy] = useState(false);

  // Reset guard
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetBusy, setResetBusy] = useState(false);

  // New-contest modal
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", blurb: "", target_listing_id: "", target_position: "image-bottom" });
  const [newBusy, setNewBusy] = useState(false);
  const [startWeekBusy, setStartWeekBusy] = useState(false);

  const contest = contestSummary?.contest;

  // ---------- Loaders ----------
  const loadSummary = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/contests/needle/current");
      setContestSummary(data);
      if (data?.contest) {
        setForm({
          name: data.contest.name || "",
          blurb: data.contest.blurb || "",
          target_listing_id: data.contest.target_listing_id || "",
          target_position: data.contest.target_position || "image-bottom",
          is_closed: !!data.contest.is_closed,
        });
      }
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  const loadEntries = async () => {
    try {
      const { data } = await api.get("/admin/contests/needle/entries");
      setEntries(data?.entries || []);
    } catch (err) {
      console.error("entries load failed", err);
    }
  };

  const loadListings = async () => {
    try {
      const params = { limit: 100 };
      if (sportFilter) params.sport = sportFilter;
      const { data } = await api.get("/listings", { params });
      setListings(data?.items || []);
    } catch (err) {
      console.error("listings fetch failed", err);
    }
  };

  const loadSports = async () => {
    // Pull the definitive sport list from the same facets endpoint that
    // Browse uses so the dropdown always matches what's visible to
    // members — never a subset derived from the current listing page.
    try {
      const { data } = await api.get("/listings/facets");
      const sportEntries = Object.entries(data?.sports || {})
        .filter(([, n]) => n > 0)
        .sort((a, b) => b[1] - a[1]); // most listings first
      setSports(sportEntries.map(([sport, n]) => ({ sport, count: n })));
    } catch (err) {
      console.error("facets fetch failed", err);
    }
  };

  useEffect(() => { loadSummary(); loadEntries(); loadSports(); }, []);
  useEffect(() => { loadListings(); }, [sportFilter]);

  // Derived sports list from facets (with count for the dropdown label).
  const sportOptions = useMemo(() => sports, [sports]);

  // ---------- Actions ----------
  const saveContest = async () => {
    if (!contest) return;
    setSaving(true);
    try {
      await api.patch("/admin/contests/needle/current", form);
      toast.success("Contest updated");
      await loadSummary();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  const doRandomize = async () => {
    setRandomBusy(true);
    try {
      const { data } = await api.post("/admin/contests/needle/randomize", {
        sport: sportFilter || null,
      });
      toast.success(`🎲 Picked "${data.listing_title}" — needle at ${data.target_position}`);
      await loadSummary();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setRandomBusy(false);
    }
  };

  const doStartWeek = async () => {
    const nextWeek = (contestSummary?.contest?.week_number || 0) + 1;
    if (!confirm(
      `Start Week ${nextWeek}?\n\nThis will:\n` +
      `  • Open entries (auto-closes Sun 12:00 AM CT)\n` +
      `  • Randomize the needle${sportFilter ? ` from the ${sportFilter} category` : ""}\n\n` +
      `Existing entries from previous weeks stay in place.`
    )) return;
    setStartWeekBusy(true);
    try {
      const { data } = await api.post("/admin/contests/needle/start-week", { sport: sportFilter || null });
      toast.success(`▶︎ Week ${data.contest.week_number} started — needle: "${data.picked.listing_title}"`);
      await loadSummary();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setStartWeekBusy(false);
    }
  };

  const doBroadcast = async () => {
    if (!broadcastSubject.trim() || !broadcastBody.trim()) {
      toast.error("Subject and body are required.");
      return;
    }
    if (!contestSummary?.opt_in_count) {
      toast.error("No opted-in recipients yet.");
      return;
    }
    if (!confirm(`Send this email to ${contestSummary.opt_in_count} opted-in entrant(s)?`)) return;
    setBroadcastBusy(true);
    try {
      const { data } = await api.post("/admin/contests/needle/broadcast", {
        subject: broadcastSubject,
        body: broadcastBody,
      });
      toast.success(`Sent to ${data.sent} of ${data.recipients}${data.failed ? ` (${data.failed} failed)` : ""}`);
      setBroadcastSubject("");
      setBroadcastBody("");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBroadcastBusy(false);
    }
  };

  const doReset = async () => {
    setResetBusy(true);
    try {
      const { data } = await api.post("/admin/contests/needle/reset-entries", { confirm: resetConfirm });
      toast.success(`Deleted ${data.deleted} entries.`);
      setResetConfirm("");
      await loadSummary();
      await loadEntries();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setResetBusy(false);
    }
  };

  const createNewContest = async () => {
    if (!newForm.name.trim() || !newForm.blurb.trim()) {
      toast.error("Name and blurb are required.");
      return;
    }
    if (!confirm("Starting a new contest ARCHIVES the current one and DELETES all its entries. Continue?")) return;
    setNewBusy(true);
    try {
      await api.post("/admin/contests/needle", newForm);
      toast.success("New contest started — entries cleared.");
      setNewModalOpen(false);
      setNewForm({ name: "", blurb: "", target_listing_id: "", target_position: "image-bottom" });
      await loadSummary();
      await loadEntries();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setNewBusy(false);
    }
  };

  const downloadCsv = () => {
    // Send an authenticated fetch, then trigger a client-side download.
    // Simpler alternative: open the URL — but that skips the Bearer header.
    api
      .get("/admin/contests/needle/entries.csv", { responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `hunt57-${contest?.name?.replace(/\s+/g, "_") || "entries"}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((err) => toast.error(formatErr(err)));
  };

  // ---------- Render ----------
  if (loading) {
    return <div className="mt-6 text-zinc-500 font-mono text-xs uppercase tracking-widest">Loading contest…</div>;
  }

  if (!contest) {
    return (
      <div className="mt-6 space-y-4">
        <div className="h57-surface p-6 text-center" data-testid="admin-contest-empty">
          <h3 className="font-display font-bold text-lg">No contest is active</h3>
          <p className="mt-1 text-sm text-zinc-400">Start a Hunt-the-Needle contest to begin capturing entries.</p>
          <button
            onClick={() => setNewModalOpen(true)}
            data-testid="admin-contest-start-btn"
            className="h57-btn-primary mt-4"
          >
            Start a contest
          </button>
        </div>
        {newModalOpen && <NewContestModal form={newForm} setForm={setNewForm} busy={newBusy} onCancel={() => setNewModalOpen(false)} onSubmit={createNewContest} />}
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-6" data-testid="admin-contest">
      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCell label="Contest" value={contest.name || "—"} testId="admin-contest-name" />
        <StatCell
          label="Week"
          value={contest.week_number ? `Week ${contest.week_number}` : "Not started"}
          accent={contest.week_number ? "text-white" : "text-zinc-500"}
          testId="admin-contest-week"
        />
        <StatCell
          label={contest.is_week_open ? "Entries (this week)" : "Entries (total)"}
          value={contest.is_week_open ? contestSummary.entry_count_this_week : contestSummary.entry_count_total}
          accent="text-emerald-400"
          testId="admin-contest-entry-count"
        />
        <StatCell label="Opted In" value={contestSummary.opt_in_count} accent="text-[#FFD700]" testId="admin-contest-optin-count" />
        <StatCell
          label="Status"
          value={contest.is_week_open ? "OPEN" : "CLOSED"}
          accent={contest.is_week_open ? "text-emerald-400" : "text-red-400"}
          testId="admin-contest-status"
        />
      </div>

      {/* Weekly control panel — the primary Sunday-afternoon action. */}
      <section className="h57-surface p-5 border border-[#FFD700]/30 space-y-3" data-testid="admin-contest-week-panel">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display font-bold text-sm uppercase tracking-widest text-[#FFD700]">
              ▶︎ Week Control
            </h3>
            {contest.is_week_open ? (
              <p className="mt-1 text-xs text-zinc-400 leading-snug">
                Week {contest.week_number} is <span className="text-emerald-400">OPEN</span>. Entries auto-close on{" "}
                <span className="text-white font-mono">
                  {contest.week_closes_at
                    ? new Date(contest.week_closes_at).toLocaleString("en-US", { timeZone: "America/Chicago", weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" })
                    : "—"}
                </span>.
              </p>
            ) : (
              <p className="mt-1 text-xs text-zinc-400 leading-snug">
                {contest.week_number === 0
                  ? "No week is open yet. Click Start Week 1 to open entries + drop the first needle."
                  : `Week ${contest.week_number} is closed. Click Start Week ${contest.week_number + 1} on Sunday to open entries + drop a new needle — or start a new contest below to reset entries.`}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={doStartWeek}
            disabled={startWeekBusy}
            data-testid="admin-contest-start-week-btn"
            className="h57-btn-primary text-xs whitespace-nowrap"
          >
            {startWeekBusy ? "Starting…" : `Start Week ${(contest.week_number || 0) + 1}`}
          </button>
        </div>
      </section>

      {/* Contest editor */}
      <section className="h57-surface p-5 space-y-3" data-testid="admin-contest-editor">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-sm uppercase tracking-widest">Contest Settings</h3>
          <button onClick={() => setNewModalOpen(true)} className="h57-btn-secondary text-xs" data-testid="admin-contest-new-btn">
            Start NEW contest (clears entries)
          </button>
        </div>

        <div>
          <label className="h57-label">Name</label>
          <input className="h57-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-contest-name-input" placeholder="e.g. Winter 2026 Giveaway" />
        </div>

        <div>
          <label className="h57-label">Blurb (shown on Browse banner + the &quot;You Found It&quot; page)</label>
          <textarea rows={4} className="h57-input resize-y font-mono text-xs" value={form.blurb} onChange={(e) => setForm({ ...form, blurb: e.target.value })} data-testid="admin-contest-blurb-input" placeholder="$25 eBay gift card. Runs Feb 20 – Mar 20. Winner announced Mar 21." />
        </div>

        {/* Listing picker */}
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="h57-label">Filter listings by sport</label>
            <select className="h57-input" value={sportFilter} onChange={(e) => setSportFilter(e.target.value)} data-testid="admin-contest-sport-filter">
              <option value="">— All sports ({sportOptions.reduce((a, s) => a + s.count, 0)}) —</option>
              {sportOptions.map((s) => (
                <option key={s.sport} value={s.sport}>{s.sport} ({s.count})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="h57-label">Position on the listing page</label>
            <select className="h57-input" value={form.target_position} onChange={(e) => setForm({ ...form, target_position: e.target.value })} data-testid="admin-contest-position-select">
              {POSITION_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        </div>

        {/* Randomize — mid-week manual rotation (rare — normally handled by Start Week above). */}
        <div className="flex items-center justify-between gap-3 p-3 border border-dashed border-white/15 rounded">
          <div className="min-w-0">
            <div className="font-display font-bold text-xs uppercase tracking-widest text-zinc-300">🎲 Randomize needle (this week)</div>
            <p className="mt-0.5 text-[11px] text-zinc-500 leading-snug">
              Only re-rolls the needle&apos;s listing + zone within the CURRENT week — doesn&apos;t open a new week. Use this if you want to swap the target mid-week without extending the deadline.
            </p>
          </div>
          <button
            type="button"
            onClick={doRandomize}
            disabled={randomBusy}
            data-testid="admin-contest-randomize-btn"
            className="h57-btn-secondary text-xs whitespace-nowrap"
          >
            {randomBusy ? "Rolling…" : "Randomize"}
          </button>
        </div>

        <div>
          <label className="h57-label">Needle target listing (this week&apos;s location)</label>
          <input className="h57-input font-mono text-xs" value={form.target_listing_id} onChange={(e) => setForm({ ...form, target_listing_id: e.target.value })} data-testid="admin-contest-listing-id-input" placeholder="Paste a listing ID or click a card below" />
          {form.target_listing_id && (
            <a href={`/listing/${form.target_listing_id}`} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-[11px] text-[#FFD700] hover:underline" data-testid="admin-contest-preview-link">
              → Open target listing in new tab to preview placement
            </a>
          )}
          <div className="mt-2 max-h-64 overflow-y-auto grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="admin-contest-listing-picker">
            {listings.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => setForm({ ...form, target_listing_id: l.id })}
                data-testid={`admin-contest-pick-${l.id}`}
                className={`h57-surface p-1 text-left transition ${form.target_listing_id === l.id ? "ring-2 ring-[#FFD700]" : "hover:ring-1 hover:ring-white/20"}`}
                title={l.title}
              >
                <div className="aspect-square bg-[#0A0A0A] overflow-hidden">
                  {l.front_image ? (
                    <img src={imgUrl(l.front_image, { w: 200 })} alt="" className="w-full h-full object-contain" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[8px] text-zinc-700">no image</div>
                  )}
                </div>
                <div className="mt-1 truncate text-[10px] text-zinc-400" title={l.title}>{l.title}</div>
              </button>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_closed} onChange={(e) => setForm({ ...form, is_closed: e.target.checked })} data-testid="admin-contest-closed-checkbox" className="accent-[#FFD700]" />
          <span>Contest closed (no new entries accepted)</span>
        </label>

        <div className="flex gap-2">
          <button onClick={saveContest} disabled={saving} data-testid="admin-contest-save-btn" className="h57-btn-primary">
            {saving ? "Saving…" : "Save changes"}
          </button>
          <button onClick={downloadCsv} data-testid="admin-contest-csv-btn" className="h57-btn-secondary">
            Download entries CSV
          </button>
        </div>
      </section>

      {/* Broadcast composer */}
      <section className="h57-surface p-5 space-y-3" data-testid="admin-contest-broadcast">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-sm uppercase tracking-widest">Email opted-in entrants</h3>
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            {contestSummary.opt_in_count} recipient{contestSummary.opt_in_count === 1 ? "" : "s"}
          </span>
        </div>
        <div>
          <label className="h57-label">Subject</label>
          <input className="h57-input" value={broadcastSubject} onChange={(e) => setBroadcastSubject(e.target.value)} data-testid="admin-contest-broadcast-subject" placeholder="Hunt57 giveaway — winner announcement" />
        </div>
        <div>
          <label className="h57-label">Message</label>
          <textarea rows={6} className="h57-input resize-y" value={broadcastBody} onChange={(e) => setBroadcastBody(e.target.value)} data-testid="admin-contest-broadcast-body" placeholder="Congrats to the winner…" />
        </div>
        <button onClick={doBroadcast} disabled={broadcastBusy || contestSummary.opt_in_count === 0} data-testid="admin-contest-broadcast-btn" className="h57-btn-primary">
          {broadcastBusy ? "Sending…" : `Send to ${contestSummary.opt_in_count} entrant${contestSummary.opt_in_count === 1 ? "" : "s"}`}
        </button>
      </section>

      {/* Entries table */}
      <section className="h57-surface p-5" data-testid="admin-contest-entries-table">
        <h3 className="font-display font-bold text-sm uppercase tracking-widest mb-3">Entrants ({entries.length})</h3>
        {entries.length === 0 ? (
          <p className="text-sm text-zinc-500">No entries yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                  <th className="py-2 pr-3">#</th>
                  <th className="py-2 pr-3">Wk</th>
                  <th className="py-2 pr-3">Username</th>
                  <th className="py-2 pr-3">Email</th>
                  <th className="py-2 pr-3">Opted&nbsp;In</th>
                  <th className="py-2 pr-3">Found At</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} data-testid={`admin-contest-entry-${e.id}`} className="border-b border-white/5">
                    <td className="py-2 pr-3 font-mono text-xs">{e.entry_number}</td>
                    <td className="py-2 pr-3 font-mono text-xs text-zinc-400">{e.week_number || "—"}</td>
                    <td className="py-2 pr-3">{e.username || "—"}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{e.user_email || "—"}</td>
                    <td className="py-2 pr-3">
                      {e.opted_in ? <span className="text-emerald-400 font-mono text-xs">yes</span> : <span className="text-zinc-500 font-mono text-xs">no</span>}
                    </td>
                    <td className="py-2 pr-3 font-mono text-[10px] text-zinc-400">{e.found_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Danger zone */}
      <section className="h57-surface p-5 border border-red-500/30 space-y-3" data-testid="admin-contest-danger">
        <h3 className="font-display font-bold text-sm uppercase tracking-widest text-red-400">Reset entries</h3>
        <p className="text-xs text-zinc-400">
          Deletes ALL entrants for the current contest but keeps the contest itself.
          Not for the normal weekly needle rotation — use this only if you need to redo the current contest.
          Type <span className="font-mono text-white">DELETE ENTRIES</span> to confirm.
        </p>
        <div className="flex gap-2">
          <input className="h57-input flex-1" value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} data-testid="admin-contest-reset-input" placeholder="DELETE ENTRIES" />
          <button
            onClick={doReset}
            disabled={resetBusy || resetConfirm !== "DELETE ENTRIES"}
            data-testid="admin-contest-reset-btn"
            className="h57-btn-primary !bg-red-500 !border-red-500 hover:!bg-red-600 disabled:opacity-40"
          >
            {resetBusy ? "Deleting…" : "Reset"}
          </button>
        </div>
      </section>

      {newModalOpen && <NewContestModal form={newForm} setForm={setNewForm} busy={newBusy} onCancel={() => setNewModalOpen(false)} onSubmit={createNewContest} />}
    </div>
  );
}

function StatCell({ label, value, accent, testId }) {
  return (
    <div className="h57-surface p-4" data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`mt-1 font-display font-black text-xl truncate ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}

function NewContestModal({ form, setForm, busy, onCancel, onSubmit }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="admin-contest-new-modal">
      <div className="h57-surface max-w-lg w-full p-6 space-y-3">
        <h3 className="font-display font-bold text-lg">Start a new contest</h3>
        <p className="text-xs text-zinc-400">
          The current contest will be archived and its entries deleted. Members&apos; entries are cleared for a fresh contest.
        </p>
        <div>
          <label className="h57-label">Name</label>
          <input className="h57-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-contest-new-name" placeholder="Spring 2026 Giveaway" />
        </div>
        <div>
          <label className="h57-label">Blurb</label>
          <textarea rows={3} className="h57-input resize-y font-mono text-xs" value={form.blurb} onChange={(e) => setForm({ ...form, blurb: e.target.value })} data-testid="admin-contest-new-blurb" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="h57-label">Target listing (optional)</label>
            <input className="h57-input font-mono text-xs" value={form.target_listing_id} onChange={(e) => setForm({ ...form, target_listing_id: e.target.value })} data-testid="admin-contest-new-listing" placeholder="Set later in editor" />
          </div>
          <div>
            <label className="h57-label">Position</label>
            <select className="h57-input" value={form.target_position} onChange={(e) => setForm({ ...form, target_position: e.target.value })} data-testid="admin-contest-new-position">
              {POSITION_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={onCancel} className="h57-btn-secondary flex-1" data-testid="admin-contest-new-cancel">Cancel</button>
          <button onClick={onSubmit} disabled={busy} className="h57-btn-primary flex-1" data-testid="admin-contest-new-submit">
            {busy ? "Starting…" : "Start contest"}
          </button>
        </div>
      </div>
    </div>
  );
}
